/**
 * Os cards em mosaico, logo abaixo da seção clara.
 *
 * No template são cinco retratos de capitães em alturas alternadas — dois numa
 * coluna, um no meio, dois na outra. O mosaico é o efeito: as colunas têm
 * larguras diferentes (`_01` a `_05`) e a fileira nunca fica reta, que é o que
 * dá o ar de mural em vez de grade.
 *
 * Aqui os retratos viram as equipes inscritas. É a mesma composição com o dado
 * que a MPC tem de verdade: escudo, nome e a plataforma em que o time joga.
 * Retrato de capitão exigiria foto de gente real, e a plataforma não pede foto
 * a ninguém — pedir passaria a ser um requisito de cadastro criado por uma
 * decisão de layout.
 */

import { ehEscudoPadrao } from '@mpc/core';
import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useEntradaDaSecao } from './animacoes';
import { TeamCrest } from '@/components/ui';
import { PLATFORM_LABEL } from '@/lib/format';
import { queries } from '@/lib/api';

/** O mosaico do template tem exatamente cinco lugares. */
const LUGARES = 5;

export function SecaoDestaques() {
  const raiz = useRef<HTMLElement>(null);
  const { data } = useQuery(queries.teams());

  /*
   * Os times com escudo próprio vêm primeiro.
   *
   * Num mural de cinco peças grandes, a diferença entre um escudo enviado pelo
   * capitão e um monograma gerado é a diferença entre parecer um mural e
   * parecer um placeholder. Quem subiu arte ganha a vitrine.
   */
  const times = (data?.teams ?? [])
    .slice()
    .sort((a, b) => Number(Boolean(b.crestUrl)) - Number(Boolean(a.crestUrl)))
    .slice(0, LUGARES);

  useEntradaDaSecao(raiz, '[data-entra]', { dependencias: [times.length] });

  if (times.length === 0) return null;

  /* As três colunas do mosaico: 2 cards, 1 card, 2 cards. */
  const colunas = [times.slice(0, 2), times.slice(2, 3), times.slice(3, 5)];

  return (
    <section className="matches-fixtures-section">
      <div className="fixtures-top-gradient" aria-hidden />
      <div className="fixtures-bottom-gradient" aria-hidden />

      <div className="container">
        <div className="matches-fixtures-wrapper">
          <h2 className="fixtures-title" data-entra>
            As equipes da competição
          </h2>
          <p className="large-paragraph fixtures-para" data-entra>
            Cada clube com elenco, histórico e as estatísticas que vêm direto do EA SPORTS FC.
          </p>
          <div className="fixtures-buttons" data-entra>
            <Link to="/times" className="transparent-button">
              <div className="transparent-button-text">Ver todos os times</div>
              <div className="transparent-button-bg" aria-hidden />
            </Link>
          </div>
        </div>

        <div className="fixtures-cards">
          {colunas.map((coluna, indiceDaColuna) => (
            <div key={indiceDaColuna} className={`fixtures-cards-block _0${indiceDaColuna + 1}`} data-entra>
              {coluna.map((time, indiceNaColuna) => {
                /* A numeração do card é contínua entre as colunas: é ela que o
                   template usa para dar altura diferente a cada peça. */
                const posicao = colunas
                  .slice(0, indiceDaColuna)
                  .reduce((total, c) => total + c.length, 0) + indiceNaColuna + 1;

                return (
                  <Link
                    key={time.id}
                    to={`/times/${time.slug}`}
                    className={`fixtures-card _0${posicao}`}
                  >
                    {time.crestUrl && !ehEscudoPadrao(time.crestUrl) ? (
                      <img
                        src={time.crestUrl}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        className={`fixture-member-image _0${posicao}`}
                      />
                    ) : (
                      <div className={`fixture-member-image _0${posicao} escudo-vazio`}>
                        <TeamCrest team={time} size="xl" />
                      </div>
                    )}

                    <div className="fixture-card-detail">
                      <div className="fixture-member-name">{time.name}</div>
                      <p className="fixture-member-designation">
                        {PLATFORM_LABEL[time.platform]}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
